import React from "react";
import {Button, Column, Row, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {RunInAction_Set} from "web-vcore";
import {store} from "../../../Store/index.js";
import {Story} from "../../../Server/DB/stories.js";
import {StoryMessage, StoryMessageProto} from "../../../Server/DB/storyMessages/@StoryMessage.js";
import {FindMessagesInChain} from "../../../Server/DB/storyMessages.js";
import {AddStoryMessage} from "../../../Server/Commands/StoryMessageCommands.js";
import {SessionLog} from "../../Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {GenerateNextMessageOptions} from "../../../Utils/AI/LLMs/@Shared.js";
import {GenerateNextMessage_Generic} from "../../../Utils/AI/LLMs/@Shared.js";
import {ScrollView} from "react-vscrollview";

type SummarizeRangeDialogOptions_Nullable = {
	[K in keyof SummarizeRangeDialogOptions]: SummarizeRangeDialogOptions[K]|n;
};
type SummarizeRangeDialogOptions_Init = ConstructorParameters<typeof SummarizeRangeDialogOptions>[0];
export class SummarizeRangeDialogOptions {
	constructor(data: RequiredBy<Partial<SummarizeRangeDialogOptions_Nullable>, "story" | "messages">) {
		const d = data;
		Object.assign(this, {
			story: d.story!,
			messages: d.messages!,
			suggestedPartNumber: d.suggestedPartNumber ?? undefined,
			messagesToSummarize_firstId: d.messagesToSummarize_firstId ?? "",
			messagesToSummarize_lastId: d.messagesToSummarize_lastId ?? "",
			llmInstructionsMessage: d.llmInstructionsMessage ?? `
				LLM, please compress the events from all the messages above into a timestamped outline titled "Events from Part ${d.suggestedPartNumber ?? "?"} of the story".

				Rules:
				* Don't omit any events in your timeline. It's fine for the outline to have dozens of events in it.
				* Start each event's text with the time/date stamp of that event, as seen within the original messages.
				* Each event should be placed on its own line, consisting of about 50 words.
				* IMPORTANT: Be VERY succinct. Cut out all unnecessary words, even if it means some slightly-off grammar.
				
				Example line in timeline: "${""
				}[3:23 PM, Sunday] Alice asks where object came from (2yr ago). Shopkeeper: Bottle in ocean. Alice and Bob exit into alley.${""
				} Alice connects to recent ocean events (storms, odd reflections, distant ships); notes Monday will require long explanation to employer.${""
				} They walk home; evening sky. Bob: Shopkeeper observant, but unlikely to be threat."
			`.AsMultiline(0),
			afterCreate_customStep: d.afterCreate_customStep ?? undefined,
			//afterCreate_showCompletionDialog: d.afterCreate_showCompletionDialog ?? true,
			afterCreate_uiAddition: d.afterCreate_uiAddition ?? undefined,
		} satisfies SummarizeRangeDialogOptions);
	}
	story!: Story;
	messages!: StoryMessage[];
	suggestedPartNumber: number|n;
	messagesToSummarize_firstId!: string;
	messagesToSummarize_lastId!: string;
	llmInstructionsMessage!: string;
	/** Called (and awaited) after the new summary message has been created and uploaded, but before process marked "done". */
	afterCreate_customStep: ((newSummaryId: string, firstMessage: StoryMessage, lastMessage: StoryMessage)=>void|Promise<void>)|n;
	//afterCreate_showCompletionDialog!: boolean;
	afterCreate_uiAddition: React.FunctionComponent|n;
}

export function ShowSummarizeRangeDialog(opts_init: SummarizeRangeDialogOptions_Init) {
	const opts = new SummarizeRangeDialogOptions(opts_init);
	const {story, messages} = opts;
	const uiState = store.main.content;
	const controller = ShowMessageBox({
		title: "Summarize range of messages", cancelButton: true,
		okOnEnterKey: false,
		message: ()=>{
			const Change = (..._)=>controller.UpdateUI();
			return (
				<Column style={{width: 600}}>
					<Row>
						<Text>First ID: </Text>
						<TextInput style={{flex: 1}} value={opts.messagesToSummarize_firstId} onChange={val=>Change(opts.messagesToSummarize_firstId = val)}/>
					</Row>
					<Row mt={5}>
						<Text>Last ID: </Text>
						<TextInput style={{flex: 1}} value={opts.messagesToSummarize_lastId} onChange={val=>Change(opts.messagesToSummarize_lastId = val)}/>
					</Row>
					<Row mt={5}>
						<Text>Response tokens: </Text>
						<Spinner style={{flex: 1}} min={1} value={uiState.summarize_responseTokens} onChange={val=>Change(RunInAction_Set(()=>uiState.summarize_responseTokens = val))}/>
					</Row>
					<Row mt={5}>LLM instructions message:</Row>
					<TextArea autoSize={true} value={opts.llmInstructionsMessage ?? ""} onChange={val=>Change(opts.llmInstructionsMessage = val)}/>
				</Column>
			);
		},
		onOK: async()=>{
			const firstMessage = messages.find(a=>a.id == opts.messagesToSummarize_firstId);
			if (firstMessage == null) return void ShowMessageBox({title: "Error", message: "First message not found."});
			const lastMessage = messages.find(a=>a.id == opts.messagesToSummarize_lastId);
			if (lastMessage == null) return void ShowMessageBox({title: "Error", message: "Last message not found."});
			const messageChainToSummarize = FindMessagesInChain(messages, lastMessage, true, a=>a.id == opts.messagesToSummarize_firstId);

			const messageChainToSummarize_plusSummarizeCommand = [
				...messageChainToSummarize,
				new StoryMessageProto({role: "user", content: opts.llmInstructionsMessage ?? "", model: null}),
			];

			// progress tracking
			const progress = {thinkingTokens: 0, thinkingText: "", responseTokens: 0, responseText: "", uploading: false, done: false, summaryId: "" as string};
			const progressController = ShowMessageBox({
				title: "Summarizing...",
				message: ()=>{
					return (
						<Column style={{minWidth: 300}}>
							<Row>
								<Text>Thinking tokens received:</Text>
								<Button ml={5} text={`${progress.thinkingTokens}`} enabled={progress.thinkingTokens > 0} onClick={()=>{
									ShowMessageBox({
										title: "Thinking text",
										message: ()=><ScrollView style={{maxHeight: 500}}><Text sel style={{whiteSpace: "pre-wrap", maxWidth: 700}}>{progress.thinkingText}</Text></ScrollView>
									})
								}}/>
							</Row>
							<Row mt={5}>
								<Text>Response tokens received:</Text>
								<Button ml={5} text={`${progress.responseTokens}`} enabled={progress.responseTokens > 0} onClick={()=>{
									ShowMessageBox({
										title: "Response text",
										message: ()=><ScrollView style={{maxHeight: 500}}><Text sel style={{whiteSpace: "pre-wrap", maxWidth: 700}}>{progress.responseText}</Text></ScrollView>
									})
								}}/>
							</Row>
							{progress.uploading && !progress.done &&
							<Row mt={10} style={{fontStyle: "italic"}}>Uploading story message...</Row>}
							{progress.done && <>
								<Row mt={10} style={{color: "green", fontWeight: "bold"}}>Summary created successfully!</Row>
								<Row mt={5}><Text sel>ID: {progress.summaryId}</Text></Row>
								<Row mt={5}><Text sel>First message: {firstMessage.id}</Text></Row>
								<Row mt={5}><Text sel>Last message: {lastMessage.id}</Text></Row>
								{opts.afterCreate_uiAddition && <opts.afterCreate_uiAddition/>}
							</>}
						</Column>
					);
				},
			});

			const summaryMessage = new StoryMessageProto({
				role: "assistant", content: "", partial: true, isSummary: true,
				model: null, // populated soon below (after options init)
			});

			const options = GenerateNextMessageOptions.New_UsingSettings({
				minNumberedListSize: 999, dynamicListNumbers: [], llmHistory_keepLastXNumberedLists: 999, temperature: 1,
				responseTokens: uiState.summarize_responseTokens,

				prevMessages: messageChainToSummarize_plusSummarizeCommand,
				onTextSegment: nextTextSegment=>{
					summaryMessage.content += nextTextSegment;
					progress.responseTokens += nextTextSegment.length;
					progress.responseText += nextTextSegment;
					progressController.UpdateUI();
				},
				onThinkingSegment: nextThinkingSegment=>{
					progress.thinkingTokens += nextThinkingSegment.length;
					progress.thinkingText += nextThinkingSegment;
					progressController.UpdateUI();
				},
			});
			summaryMessage.model = options.model; // record model used for generation
			await GenerateNextMessage_Generic(options);

			console.log("Summarizing for chain:", messageChainToSummarize, "@Summary:", summaryMessage);

			progress.uploading = true;
			progressController.UpdateUI();

			const summaryMessage_final = await new AddStoryMessage({entry: new StoryMessage({
				// proto data
				role: summaryMessage.role,
				content: summaryMessage.content,
				model: summaryMessage.model,
				// extra data
				story: story.id,
				parent: firstMessage.parent,
				summary_chainEnd: lastMessage.id,
				summary_enabled: true,
			})}).RunOnServer();
			const summaryMessage_final_id = summaryMessage_final.id;
			SessionLog(`Uploaded new story message (type: summary): ${summaryMessage_final_id}`);

			await opts.afterCreate_customStep?.(summaryMessage_final_id, firstMessage, lastMessage);

			progress.done = true;
			progress.summaryId = summaryMessage_final_id;
			progressController.UpdateOptions({title: "Summary created"});
			progressController.UpdateUI();
		},
	});
}